import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../api/client.js'
import { useAuthStore } from '../store/authStore.js'
import { useCallbacksStore } from '../store/callbacksStore.js'
import type { Callback } from '../components/admin/callbacks/CallbackDrawer.js'

export type { Callback }

const POLL_INTERVAL_MS = 60_000

/**
 * Fetches the current agent's PENDING callbacks from
 * GET /callbacks?status=PENDING&agentId=<sub>&limit=20
 * and polls every 60 seconds for updates.
 *
 * UI-3c Task 7: the list lives in the singleton `callbacksStore`. This hook
 * fetches + writes via `setMyCallbacks` and returns the list by subscribing
 * to the store, so socket-driven patches (`callback:scheduled` / `:originated`
 * / `:failed`) reach consumers automatically. `loading` stays local.
 */
export function useMyCallbacks(): {
  callbacks: Callback[]
  loading: boolean
  refetch: () => Promise<void>
} {
  const agentId = useAuthStore((s) => s.user?.sub) ?? ''
  const callbacks = useCallbacksStore((s) => s.myCallbacks)
  const [loading, setLoading] = useState(true)

  // Mounted guard: prevents setState calls after unmount.
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const load = useCallback(async (): Promise<void> => {
    if (!agentId) {
      // No authenticated agent — clear loading and return without fetching.
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const resp = await api.get<{ callbacks: Callback[] }>(
        `/callbacks?status=PENDING&agentId=${agentId}&limit=20`,
      )
      if (mountedRef.current) {
        useCallbacksStore.getState().setMyCallbacks(resp.callbacks)
      }
    } catch {
      // Leave the store list as-is on error; just stop loading. The hook has no
      // `error` field in its return type, so we swallow and log only in dev.
      if (import.meta.env.DEV) {
        console.warn('[useMyCallbacks] fetch failed')
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false)
      }
    }
  }, [agentId])

  // Initial fetch + 60-second poll.
  useEffect(() => {
    void load()
    const id = setInterval(() => void load(), POLL_INTERVAL_MS)
    return () => clearInterval(id)
  }, [load])

  return { callbacks, loading, refetch: load }
}
